/**
 * sourcing/types.ts — 소싱 페이지의 state 정의 + factory + 도메인 상수
 *
 * SourcingView.ts 에서 분리 (M4, 2026-07-08 — deal-detail 분해 계획 v2 패턴 이식).
 * 이동만 — 동작 변화 없음. render-* 모듈들이 view 를 거치지 않고
 * 여기서 직접 import 하기 위한 공유 지점 (순환 import 방지).
 */

import * as api from '../../api/client';

// ─── 라우트 loader 계약 ─────────────────────────────────────────────
/** 라우트 loader 의 반환 타입. main.ts 의 loader 와 공유. */
export type SourcingLoaded = api.SourcingListResponse;

// ─── 니즈 생성 모달 — 독립 니즈(딜 미연결) 기준 ─────────────────────
export interface CreateState {
  open:        boolean;
  optionPart:  string;
  itemText:    string;
  category:    string;
  quantity:    string;
  condition:   string;
  salesTags:   string[];
  memoTags:    string[];
  // 품목 검색(딜 상세 니즈검색 재사용) — 선택 시 optionPart/itemText/category 자동 채움.
  query:       string;
  searching:   boolean;
  hits:        api.CatalogSearchHit[];
  saving:      boolean;
  error:       string;
}
export function emptyCreate(): CreateState {
  return {
    open: false, optionPart: '', itemText: '', category: '',
    quantity: '1', condition: '', salesTags: [], memoTags: [],
    query: '', searching: false, hits: [],
    saving: false, error: '',
  };
}

// ─── 도메인 상수 ────────────────────────────────────────────────────
export const CONDITION_OPTIONS = ['F/S', 'Newbulk', 'Newpull', 'REF', '신품', '중고'];
// 대체 컨디션(매입처 제안) — 기획 popover 기준.
export const SOURCED_CONDITION_OPTIONS = ['F/S', 'F/S Spare', 'NOB', 'Newbulk', 'Newpull', 'Bulk', 'REF'];
export const RFQ_MEMO_TAGS = ['Landed', '사진받음'] as const;

// ─── 가격 응답 popover — 매입처 버블(rfq 1건) 클릭 시 ───────────────
export interface PriceState {
  open:         boolean;
  rfqId:        string;
  supplierName: string;
  amount:       string;
  currency:     string;          // 'USD' | 'KRW'
  noStock:      boolean;
  condition:    string;          // 대체 컨디션 (sourced_condition)
  respondedQty: string;          // 회신 수량 (responded_quantity) — 부족 공급 (마이그195)
  requestedQty: number;          // 니즈 요청 수량(표시·비교용, 저장 안 함)
  altPart:      string;          // 대체품목 (alternative_part)
  altResults:   api.CatalogSearchHit[];   // 대체품목 검색 결과
  altSearching: boolean;
  tags:         string[];        // RFQ 메모 (rfq_memo_tags)
  tagsOpen:     boolean;
  memo:         string;          // 기타 메모 (request_note)
  x:            number;          // 앵커(버블) 좌측
  y:            number;          // 앵커 하단 (아래 배치 기준)
  anchorTop:    number;          // 앵커 상단 (위로 뒤집을 때 기준)
  offX:         number;          // containing-block 보정 (실측 오프셋)
  offY:         number;
  saving:       boolean;
  error:        string;
  purchaseRequested: boolean;    // 이 거래처 구매요청 상태 (마이그140)
  attachments:  api.SourcingRfqBubble['attachments'];  // 거래처 수신 이미지/PDF (마이그141)
  uploading:    boolean;         // 첨부 업로드 진행중
}
export function emptyPrice(): PriceState {
  return {
    open: false, rfqId: '', supplierName: '', amount: '', currency: 'USD',
    noStock: false, condition: '', respondedQty: '', requestedQty: 0, altPart: '', altResults: [], altSearching: false,
    tags: [], tagsOpen: false, memo: '',
    x: 0, y: 0, anchorTop: 0, offX: 0, offY: 0, saving: false, error: '',
    purchaseRequested: false,
    attachments: [], uploading: false,
  };
}

// ─── 드래그 대상 — 니즈 1개 또는 그룹 전체 ──────────────────────────
export type DragPayload = { type: 'need'; id: string } | { type: 'group'; key: string };

// ─── 매입처 지정 모달 — 클러스터(여러 니즈)에 매입처 집합을 지정 ────
export interface SupplierModalState {
  open:      boolean;
  needIds:   string[];                 // 클러스터의 니즈 id 들
  label:     string;                   // 부제 표시용 (예: "3건")
  all:       api.SupplierPick[];       // 활성 매입처 전체
  selected:  string[];                 // 선택된 매입처 id (순서 = 출력 순서)
  search:    string;
  loading:   boolean;
  saving:    boolean;
  error:     string;
}
export function emptySupplierModal(): SupplierModalState {
  return { open: false, needIds: [], label: '', all: [], selected: [], search: '', loading: false, saving: false, error: '' };
}

// 개별 니즈 거래처 표시 설정(visibility) 모달 — 그룹 매입처 중 표시할 것만(마이그142).
export interface VisibilityModalState {
  open:      boolean;
  needId:    string;
  label:     string;                   // 니즈 표시명(파트넘버 등)
  item:      string;                   // 이 니즈에서 요청하는 품목(파트/설명) — 헤더/행 공용
  suppliers: Array<{
    supplier_id:   string;
    supplier_name: string;
    visible:       boolean;
    has_data:      boolean;
    status:        string;             // 이 매입처 rfq 상태(문의예정/가격수신/재고없음 등)
    price:         string;             // 표시용 가격(있으면)
  }>;
  search:    string;
  saving:    boolean;
  error:     string;
}
export function emptyVisibilityModal(): VisibilityModalState {
  return { open: false, needId: '', label: '', item: '', suppliers: [], search: '', saving: false, error: '' };
}

// ─── 뷰 전체 state ──────────────────────────────────────────────────
export interface SourcingData {
  needs:        api.SourcingListNeed[];
  nextCursor:   api.SourcingCursor | null;
  loadingMore:  boolean;
  search:       string;
  loading:      boolean;
  menuId:       string | null;
  create:       CreateState;
  supplierModal: SupplierModalState;
  visibilityModal: VisibilityModalState;
  price:         PriceState;
  // 태그 편집 드롭다운 (영업요청/소싱메모 셀 클릭)
  tagEditor:    { needId: string; kind: 'sales' | 'memo'; x: number; y: number } | null;
  // 드래그 묶기 확인 팝오버
  mergeConfirm: { source: DragPayload; targetId: string; x: number; y: number } | null;
}
